import {
  ALERT_SEARCH_CONDITIONS,
  CASE_SEARCH_CONDITIONS,
  ENTITY_SEARCH_CONDITIONS,
  NOTES_SEARCH_CONDITIONS,
  RULE_SEARCH_CONDITIONS,
  TEAM_SEARCH_CONDITIONS,
} from 'app/modules/search/constants';
import {
  AlertSearchOptions,
  CaseSearchOptions,
  EntitySearchOptions,
  MatchType,
  NotesSearchOptions,
  OldEntitySearchType,
  RuleSearchOptions,
  TeamSearchOptions,
  TransactionEventSearchOptions,
} from 'app/modules/search/enum';
import { getDisplayName } from 'app/modules/search/helpers';
import {
  ActionEventsSearchResponse,
  ActionEventsSearchResult,
  AlertsSearchResult,
  CasesSearchResult,
  EntitiesSearchV2Response,
  EntitiesSearchV2Result,
  InstrumentsSearchResponse,
  InstrumentsSearchResult,
  NotesSearchResult,
  RulesSearchResult,
  SearchResponse,
  SearchTypes,
  TeamsSearchResult,
  TxnEventsSearchResponse,
  TxnEventsSearchResult,
} from 'app/modules/search/models';
import {
  SearchCondition,
  PossibleSearchFields,
} from 'app/modules/search/payload';
import {
  SearchV2Response,
  BaseV2Hits,
  ActionEventSource,
  InstrumentSource,
  TransactionSource,
  EntitySource,
  AlertSource,
  CaseSource,
  RuleSource,
  TeamSource,
  NotesSource,
} from 'app/modules/search/response';

export const getEntitySearchConditions = (
  phrases: string[],
  searchType?: OldEntitySearchType,
): SearchCondition<EntitySearchOptions>[] =>
  searchType
    ? ENTITY_SEARCH_CONDITIONS[searchType].map((c) => ({
        ...c,
        phrases,
      }))
    : [];

export const getTeamSearchConditions = (
  phrases: string[],
): SearchCondition<TeamSearchOptions>[] =>
  TEAM_SEARCH_CONDITIONS.map((c) => ({
    ...c,
    phrases,
  }));

export const getAlertSearchConditions = (
  phrases: string[],
): SearchCondition<AlertSearchOptions>[] =>
  ALERT_SEARCH_CONDITIONS.map((c) => ({
    ...c,
    phrases,
  }));

export const getCaseSearchConditions = (
  phrases: string[],
): SearchCondition<CaseSearchOptions>[] =>
  CASE_SEARCH_CONDITIONS.map((c) => ({
    ...c,
    phrases,
  }));

export const getRuleSearchConditions = (
  phrases: string[],
): SearchCondition<RuleSearchOptions>[] =>
  RULE_SEARCH_CONDITIONS.map((c) => ({
    ...c,
    phrases,
  }));

export const getNotesSearchConditions = (
  phrases: string[],
): SearchCondition<NotesSearchOptions>[] =>
  NOTES_SEARCH_CONDITIONS.map((c) => ({
    ...c,
    phrases,
    highlight_search_field: true,
    highlight_options: {
      fragment_size: 80,
      number_of_fragments: 1,
    },
  }));

export const getSearchCondition = (
  phrases: string[],
  entityExactMatch: boolean,
  field?: string,
  searchType?: SearchTypes,
): SearchCondition<PossibleSearchFields>[] => {
  switch (searchType) {
    case SearchTypes.ENTITIES: {
      return getEntitySearchConditions(
        phrases,
        entityExactMatch && field === 'external_id'
          ? OldEntitySearchType.EXACT_EXTERNAL_ID
          : (field as OldEntitySearchType | undefined),
      );
    }
    case SearchTypes.TRANSACTIONS:
    case SearchTypes.ACTION_EVENTS:
      return [
        {
          phrases,
          field: TransactionEventSearchOptions.EXTERNAL_ID, // The value of external_id enums are all the same
          match_type: MatchType.EXACT,
        },
      ];
    case SearchTypes.INSTRUMENTS:
      return [
        {
          phrases,
          field: TransactionEventSearchOptions.EXTERNAL_ID, // The value of external_id enums are all the same
          match_type: MatchType.PARTIAL,
        },
      ];
    case SearchTypes.ALERTS:
      return getAlertSearchConditions(phrases);
    case SearchTypes.CASES:
      return getCaseSearchConditions(phrases);
    case SearchTypes.RULES:
      return getRuleSearchConditions(phrases);
    case SearchTypes.TEAMS:
      return getTeamSearchConditions(phrases);
    case SearchTypes.NOTES:
      return getNotesSearchConditions(phrases);
    default:
      return [];
  }
};

export const convertV2Result = (
  data: SearchV2Response,
  type?: SearchTypes,
  useDlHashId: boolean = false,
): SearchResponse => {
  switch (type) {
    case SearchTypes.ENTITIES:
      return {
        data: (data.hits as BaseV2Hits<EntitySource>[]).reduce<
          EntitiesSearchV2Result[]
        >(
          (
            acc: EntitiesSearchV2Result[],
            { source }: BaseV2Hits<EntitySource>,
          ) => {
            // Branch based on feature flag
            const id = useDlHashId
              ? source.id
              : parseInt(source.unit21_id ?? '', 10);

            // For DL hash ID: always include if source.id exists
            // For legacy: only include if parseInt succeeds
            const shouldInclude = useDlHashId
              ? Boolean(source.id)
              : !isNaN(id as number);

            if (shouldInclude) {
              // This will filter out UNIT21_ID_NOT_FOUND as well

              acc.push({
                ...source,
                id,
                unit21_id: source.unit21_id!,
                first_name: source.first_name ?? undefined,
                last_name: source.last_name ?? undefined,
                middle_name: source.middle_name ?? undefined,
                user_name: source.user_name ?? undefined,
                business_name: source.business_name ?? undefined,
                name_readable: getDisplayName(source),
              });
            }
            return acc;
          },
          [],
        ),
      } satisfies EntitiesSearchV2Response;
    case SearchTypes.TRANSACTIONS:
      return {
        data: (data.hits as BaseV2Hits<TransactionSource>[]).reduce<
          TxnEventsSearchResult[]
        >((acc, { source }) => {
          const id = useDlHashId
            ? source.id
            : parseInt(source.unit21_id ?? '', 10);
          if (useDlHashId ? Boolean(source.id) : !isNaN(id as number)) {
            // external_id is already TxnEventExternalId on TransactionSource
            acc.push({ ...source, id });
          }
          return acc;
        }, []),
      } satisfies TxnEventsSearchResponse;
    case SearchTypes.ACTION_EVENTS:
      return {
        data: (data.hits as BaseV2Hits<ActionEventSource>[]).reduce<
          ActionEventsSearchResult[]
        >((acc, { source }) => {
          const id = useDlHashId
            ? source.id
            : parseInt(source.unit21_id ?? '', 10);
          if (useDlHashId ? Boolean(source.id) : !isNaN(id as number)) {
            // external_id is already ActionEventExternalId on ActionEventSource
            acc.push({ ...source, id });
          }
          return acc;
        }, []),
      } satisfies ActionEventsSearchResponse;
    case SearchTypes.INSTRUMENTS:
      return {
        data: (data.hits as BaseV2Hits<InstrumentSource>[]).reduce<
          InstrumentsSearchResult[]
        >((acc, { source }) => {
          const id = useDlHashId
            ? source.id
            : parseInt(source.unit21_id ?? '', 10);
          if (useDlHashId ? Boolean(source.id) : !isNaN(id as number)) {
            acc.push({
              id,
              external_id: source.external_id,
              instrument_type: source.instrument_type ?? '',
              name: source.name ?? '',
              // 'source' is not in the v2 InstrumentDocument index; coerce to ''
              // matching the xstr() behaviour of the v1 search formatter
              source: '',
              status: source.status ?? '',
            } satisfies InstrumentsSearchResult);
          }
          return acc;
        }, []),
      } satisfies InstrumentsSearchResponse;
    case SearchTypes.ALERTS:
      return {
        data: (data.hits as BaseV2Hits<AlertSource>[]).reduce<
          AlertsSearchResult[]
        >((acc: AlertsSearchResult[], { source }: BaseV2Hits<AlertSource>) => {
          const id = parseInt(source.id ?? '', 10);
          const queueId = parseInt(source.queue_id ?? '', 10);

          if (!isNaN(id)) {
            acc.push({
              ...source,
              id,
              queue_id: queueId,
            });
          }
          return acc;
        }, []),
      };
    case SearchTypes.CASES:
      return {
        data: (data.hits as BaseV2Hits<CaseSource>[]).reduce<
          CasesSearchResult[]
        >((acc: CasesSearchResult[], { source }: BaseV2Hits<CaseSource>) => {
          const id = parseInt(source.id ?? '', 10);
          const queueId = parseInt(source.queue_id ?? '', 10);

          if (!isNaN(id)) {
            acc.push({
              ...source,
              id,
              queue_id: queueId,
            });
          }
          return acc;
        }, []),
      };
    case SearchTypes.RULES:
      return {
        data: (data.hits as BaseV2Hits<RuleSource>[]).reduce<
          RulesSearchResult[]
        >((acc: RulesSearchResult[], { source }: BaseV2Hits<RuleSource>) => {
          const id = parseInt(source.id ?? '', 10);

          if (!isNaN(id)) {
            acc.push({
              ...source,
              id,
              is_synchronous: source.is_synchronous?.toLowerCase() === 'true',
            });
          }
          return acc;
        }, []),
        count: data.hits.length,
      };
    case SearchTypes.TEAMS:
      return {
        data: (data.hits as BaseV2Hits<TeamSource>[]).reduce<
          TeamsSearchResult[]
        >((acc: TeamsSearchResult[], { source }: BaseV2Hits<TeamSource>) => {
          const id = parseInt(source.id ?? '', 10);

          if (!isNaN(id)) {
            acc.push({
              ...source,
              id,
            });
          }
          return acc;
        }, []),
        count: data.hits.length,
      };
    case SearchTypes.NOTES:
      return {
        data: (data.hits as BaseV2Hits<NotesSource>[]).reduce<
          NotesSearchResult[]
        >(
          (
            acc: NotesSearchResult[],
            { source, highlight }: BaseV2Hits<NotesSource>,
          ) => {
            const id = parseInt(source.id ?? '', 10);

            if (!isNaN(id)) {
              acc.push({
                ...source,
                id,
                highlight: highlight.content?.[0] ?? '',
              });
            }
            return acc;
          },
          [],
        ),
        count: data.hits.length,
      };
    default:
      return { data: [] };
  }
};
